import { useQuery } from '@tanstack/react-query';
import fetchFrameworks from 'api/block/fetchFrameworks';
import API_END_POINT from 'constants/apiEndPoints';
import { PROJECT_TEMPLATE } from 'constants/commonConstants';
import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { themeTypeState } from 'state/atoms';
import { ProjectInfoFormValues } from 'types';

interface UseProjectInfoProps {
  useFormReturn: UseFormReturn<ProjectInfoFormValues>;
}

const useProjectInfo = ({ useFormReturn }: UseProjectInfoProps) => {
  const { watch, setValue, getValues } = useFormReturn;
  const isDarkMode = useRecoilValue(themeTypeState) === 'dark';
  const selectedFramework = watch('framework');

  const { data: frameworkData } = useQuery({
    queryKey: [API_END_POINT.BLOCK.FETCH_FRAMEWORKS],
    queryFn: fetchFrameworks,
  });

  const frameworkVersions = useMemo(() => {
    if (!frameworkData || !selectedFramework) return [];

    return ['1.12', ...frameworkData.frameworks[selectedFramework].filter((version) => version !== '1.12')];
  }, [frameworkData, selectedFramework]);

  const templates = useMemo(() => {
    return Object.values(PROJECT_TEMPLATE).reduce(
      (acc, cnt) => [...acc, ...cnt.templates],
      [] as {
        id: string;
        title: string;
      }[]
    );
  }, []);

  const handleFrameworkClick = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (frameworkVersions.length === 0 || getValues('frameworkVersion')) return;

    setValue('frameworkVersion', frameworkVersions[0]);
  }, [frameworkVersions, getValues, setValue]);

  return {
    ...useFormReturn,
    templates,
    isDarkMode,
    frameworkVersions,
    selectedFramework,
    handleFrameworkClick,
  };
};

export default useProjectInfo;
